<template>
  <v-container>
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="10000"
    >
      {{ snackbarText }}

      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <v-row>

      <v-col cols="12" md="6">
        <v-card variant="outlined">
          <v-card-title class="text-h6">Import Amazon Data</v-card-title>
          <v-card-text>
            <p class="mb-4">The import file should be a csv file exported from amazon seller central</p>
            <v-file-input
              v-model="amazonFile"
              accept=".txt,.csv"
              label="Select CSV or TXT file"
              :loading="importingAmazon"
              @change="handleFileChange('amazon')"
            ></v-file-input>

            <v-btn
              color="primary"
              :loading="importingAmazon"
              :disabled="!amazonFile"
              block
              @click="importAmazonData"
            >
              Import Amazon Data
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="6">
        <v-card variant="outlined">
          <v-card-title class="text-h6">Import Sales Costs</v-card-title>
          <v-card-text>
            <p>Upload a CSV file with the following columns:</p>
            <ul class="pa-4">
              <li>Column 1: Sale ID</li>
              <li>Column 2: Sale Cost</li>
            </ul>
            <p class="text-caption mb-4">Note: First row will be considered as header and skipped</p>
            
            <v-file-input
              v-model="costFile"
              accept=".csv"
              label="Select CSV file"
              :loading="importingCost"
              @change="handleFileChange('cost')"
            ></v-file-input>

            <v-btn
              color="primary"
              :loading="importingCost"
              :disabled="!costFile"
              block
              @click="importCostData"
            >
              Import Sales Cost
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card variant="outlined">
          <v-card-title class="text-h6">Import Sales Shipping</v-card-title>
          <v-card-text>
            <p>Upload a CSV file with the following columns:</p>
            <ul class="pa-4">
              <li>Column 1: Sale ID</li>
              <li>Column 2: Shipping Cost</li>
            </ul>
            <p class="text-caption mb-4">Note: First row will be considered as header and skipped</p>
            
            <v-file-input
              v-model="shippingFile"
              accept=".csv"
              label="Select CSV file"
              :loading="importingShipping"
              @change="handleFileChange('shipping')"
            ></v-file-input>

            <v-btn
              color="primary"
              :loading="importingShipping"
              :disabled="!shippingFile"
              block
              @click="importShippingData"
            >
              Import Sales Shipping
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="6">
        <v-card variant="outlined">
          <v-card-title class="text-h6">Import Sales Fees</v-card-title>
          <v-card-text>
            <p>Upload a CSV file with the following columns:</p>
            <ul class="pa-4">
              <li>Column 1: Sale ID</li>
              <li>Column 2: (Amazon/eBay) Fees</li>
            </ul>
            <p class="text-caption mb-4">Note: First row will be considered as header and skipped</p>
            
            <v-file-input
              v-model="feesFile"
              accept=".csv"
              label="Select CSV file"
              :loading="importingFees"
              @change="handleFileChange('fees')"
            ></v-file-input>

            <v-btn
              color="primary"
              :loading="importingFees"
              :disabled="!feesFile"
              block
              @click="importFeesData"
            >
              Import Sales Fees
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card variant="outlined">
          <v-card-title class="text-h6">Import Sales Data</v-card-title>
          <v-card-text>
            <p>Upload a CSV file with the following columns:</p>
            <ul class="pa-4">
              <li>Column 1: PO Numbers</li>
              <li>Column 2: Invoice Checked(Yes/No)</li>
              <li>Column 3: Confirmed Billed(Yes/No)</li>
            </ul>
            <p class="text-caption mb-4">Note: First row will be considered as header and skipped</p>
            
            <v-file-input
              v-model="dataFile"
              accept=".csv"
              label="Select CSV file"
              :loading="importing"
              @change="handleFileChange('data')"
            ></v-file-input>

            <v-btn
              color="primary"
              :loading="importing"
              :disabled="!dataFile"
              block
              @click="importData"
            >
              Import Data
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref } from 'vue'
import { storesService } from '@/services/storesService'
import { salesService } from '@/services/salesService'

const dataFile = ref(null)
const amazonFile = ref(null)
const shippingFile = ref(null)
const costFile = ref(null)
const feesFile = ref(null)
const importing = ref(false)
const importingAmazon = ref(false)
const importingShipping = ref(false)
const importingCost = ref(false)
const importingFees = ref(false)
const snackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')

const handleFileChange = (type) => {
  let file = null
  switch (type) {
    case 'data':
      file = dataFile.value
      break
    case 'amazon':
      file = amazonFile.value
      break
    case 'shipping':
      file = shippingFile.value
      break
    case 'cost':
      file = costFile.value
      break
    case 'fees':
      file = feesFile.value
      break
  }
  if (!file) return

  if (!file.type && !file.name.endsWith('.csv')) {
    snackbarText.value = 'Please upload a CSV file'
    snackbarColor.value = 'error'
    snackbar.value = true
    switch (type) {
      case 'data':
        dataFile.value = null
        break
      case 'amazon':
        amazonFile.value = null
        break
      case 'shipping':
        shippingFile.value = null
        break
      case 'fees':
        feesFile.value = null
        break
    }
  }
}

const importData = async () => {
  if (!dataFile.value) return

  importing.value = true
  try {
    const formData = new FormData()
    formData.append('file', dataFile.value)
    
    const response = await storesService.importData(formData)
    
    snackbarText.value = response.message
    snackbarColor.value = 'success'
    snackbar.value = true
    dataFile.value = null
  } catch (err) {
    console.error('Error importing orders:', err)
    snackbarText.value = 'Failed to import orders'
    snackbarColor.value = 'error'
    snackbar.value = true
  } finally {
    importing.value = false
  }
}

const importAmazonData = async () => {
  if (!amazonFile.value) return

  importingAmazon.value = true
  try {
    const formData = new FormData()
    formData.append('file', amazonFile.value)
    
    const response = await storesService.importAmazonData(formData)
    
    snackbarText.value = 'Amazon data imported successfully, ' + response.message
    snackbarColor.value = 'success'
    snackbar.value = true
    amazonFile.value = null
  } catch (err) {
    console.error('Error importing amazon data:', err)
    snackbarText.value = 'Failed to import amazon data'
    snackbarColor.value = 'error'
    snackbar.value = true
  } finally {
    importingAmazon.value = false
  }
}

const importShippingData = async () => {
  if (!shippingFile.value) return

  importingShipping.value = true
  try {
    const formData = new FormData()
    formData.append('file', shippingFile.value)

    const response = await salesService.importShippingData(formData)
    snackbarText.value = response.message
    snackbarColor.value = 'success'
    snackbar.value = true
    shippingFile.value = null
  } catch (err) {
    console.error('Error importing shipping data:', err)
    snackbarText.value = 'Failed to import shipping data'
    snackbarColor.value = 'error'
    snackbar.value = true
  } finally {
    importingShipping.value = false
  }
}

const importCostData = async () => {
  if (!costFile.value) return

  importingCost.value = true
  try {
    const formData = new FormData()
    formData.append('file', costFile.value)
    
    const response = await salesService.importCostData(formData)
    snackbarText.value = response.message
    snackbarColor.value = 'success'
    snackbar.value = true
    costFile.value = null
  } catch (err) {
    console.error('Error importing cost data:', err)
    snackbarText.value = 'Failed to import cost data'
    snackbarColor.value = 'error'
    snackbar.value = true
  } finally {
    importingCost.value = false
  }
}

const importFeesData = async () => {
  if (!feesFile.value) return

  importingFees.value = true
  try {
    const formData = new FormData()
    formData.append('file', feesFile.value)

    const response = await salesService.importFeesData(formData)
    snackbarText.value = response.message
    snackbarColor.value = 'success'
    snackbar.value = true
    feesFile.value = null
  } catch (err) {
    console.error('Error importing fees data:', err)
    snackbarText.value = 'Failed to import fees data'
    snackbarColor.value = 'error'
    snackbar.value = true
  } finally {
    importingFees.value = false
  }
}

</script> 